/**
 * The conversation transcript (prompt.md §30, §48, §77).
 *
 * WHY the timeline is a pure function of (messages, live deltas): a client can
 * disconnect mid-run and reconnect later (prompt.md §49). The durable messages
 * are the record; the streamed delta is a *view* of the run that has not been
 * persisted yet. Keeping them as two separate inputs means a reconnecting client
 * cannot end up with a half-duplicated transcript.
 */

import type { ConversationBranch, Message } from "@dayvise/domain";
import { useEffect, useRef } from "react";

import { Badge, EmptyState, Skeleton, Spinner } from "../primitives/Feedback.tsx";
import { Button } from "../primitives/Button.tsx";
import { MUTED_TEXT, stackStyle, rowStyle } from "../styles.ts";
import { messagePlainText } from "../logic/messageParts.ts";
import type { TimestampMode } from "../logic/format.ts";
import { MessageBubble } from "./MessageBubble.tsx";

export interface LiveAssistantOutput {
  /** Partial text the runtime has streamed but not yet committed. */
  readonly text: string;
  /** The run it belongs to, so a stale stream cannot attach to a new run. */
  readonly runId: string;
}

export interface ConversationTimelineProps {
  readonly messages: readonly Message[];
  readonly showReasoning?: boolean;
  readonly onDownloadFile?: (fileId: string) => void;
  readonly downloadingFileId?: string | null;
  readonly live?: LiveAssistantOutput | null;
  readonly loading?: boolean;
  readonly onLoadOlder?: () => void;
  readonly hasOlder?: boolean;
  readonly emptyTitle?: string;
  readonly emptyDescription?: string;
  /** Auto-scroll to the newest content while a run streams. */
  readonly follow?: boolean;
  /** The signed-in user's timestamp preference, applied to every message. */
  readonly timestampMode?: TimestampMode;
  /**
   * Copies one message's text with the client's clipboard. When supplied, every
   * message gets a copy control; without it no control is rendered.
   */
  readonly onCopyMessage?: (text: string) => void | Promise<void>;
}

const NEAR_BOTTOM_PX = 120;

export function ConversationTimeline({
  messages,
  showReasoning = true,
  timestampMode = "relative",
  onDownloadFile,
  downloadingFileId,
  live = null,
  loading = false,
  onLoadOlder,
  hasOlder = false,
  emptyTitle = "No messages yet",
  emptyDescription = "Send a message to start a run.",
  follow = true,
  onCopyMessage,
}: ConversationTimelineProps) {
  const endRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!follow) return;
    const container = containerRef.current;
    const end = endRef.current;
    if (!end) return;
    // Only follow when the user is already at the bottom, so reading history is
    // never yanked away by an incoming delta.
    const atBottom = container === null || container.scrollHeight - container.scrollTop - container.clientHeight < NEAR_BOTTOM_PX;
    if (atBottom) end.scrollIntoView({ block: "end" });
  }, [messages.length, live?.text, follow]);

  if (loading && messages.length === 0) {
    return (
      <div style={stackStyle("var(--dayvise-space-md)")}>
        <Skeleton lines={2} />
        <Skeleton lines={3} />
      </div>
    );
  }

  if (messages.length === 0 && (live === null || live.text.length === 0)) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }

  return (
    <div ref={containerRef} style={{ ...stackStyle("var(--dayvise-space-md)"), overflowY: "auto" }}>
      {hasOlder && onLoadOlder ? (
        <div style={rowStyle("var(--dayvise-space-xs)", "center")}>
          <Button size="sm" variant="ghost" onClick={onLoadOlder}>
            Load earlier messages
          </Button>
        </div>
      ) : null}

      {messages.map((message) => (
        <MessageBubble
          key={message.messageId}
          role={message.role}
          parts={message.parts}
          showReasoning={showReasoning}
          onDownloadFile={onDownloadFile}
          downloadingFileId={downloadingFileId}
          createdAt={message.createdAt}
          timestampMode={timestampMode}
          onCopy={onCopyMessage}
          footer={<MessageFooter message={message} />}
        />
      ))}

      {live !== null && live.text.length > 0 ? (
        <MessageBubble role="assistant" parts={[{ type: "text", text: live.text }]} streaming />
      ) : live !== null ? (
        <div style={rowStyle("var(--dayvise-space-xs)", "flex-start")}>
          <Spinner size="sm" label="The agent is working" />
        </div>
      ) : null}

      <div ref={endRef} />
    </div>
  );
}

function MessageFooter({ message }: { readonly message: Message }) {
  return (
    <span style={rowStyle("var(--dayvise-space-xs)", "flex-start")}>
      <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>#{message.sequence}</span>
      {message.runId ? <Badge tone="neutral">run</Badge> : null}
    </span>
  );
}

export interface BranchSwitcherProps {
  readonly branches: readonly ConversationBranch[];
  readonly activeBranchId: string;
  readonly onSelect: (branchId: string) => void;
}

/**
 * Branch selector (prompt.md §19).
 *
 * Parallel runs on one conversation each write their own branch, so the UI has
 * to make competing branches understandable rather than pretending there is one
 * linear history. A child branch is labelled with its parent so the fork is
 * visible.
 */
export function BranchSwitcher({ branches, activeBranchId, onSelect }: BranchSwitcherProps) {
  if (branches.length <= 1) return null;
  const byId = new Map(branches.map((branch) => [branch.branchId, branch]));
  return (
    <div style={stackStyle("var(--dayvise-space-xs)")}>
      <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>
        {branches.length} branches — parallel runs write separately and never overwrite each other
      </span>
      <div style={rowStyle("var(--dayvise-space-xs)", "flex-start")}>
        {branches.map((branch) => {
          const parent = branch.parentBranchId ? byId.get(branch.parentBranchId) : undefined;
          const label = branch.name ?? (branch.parentBranchId === null ? "main" : `${shortId(branch.branchId)} · fork of ${parent ? shortId(parent.branchId) : "earlier"}`);
          return (
            <Button
              key={branch.branchId}
              size="sm"
              variant={branch.branchId === activeBranchId ? "primary" : "secondary"}
              onClick={() => onSelect(branch.branchId)}
            >
              {label} · {branch.status}
            </Button>
          );
        })}
      </div>
    </div>
  );
}

function shortId(value: string): string {
  const suffix = value.split("_").pop() ?? value;
  return suffix.slice(0, 6);
}

export { messagePlainText };
